import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { Link, Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { HostData } from '../Customer/Data'
import { HealthCheckBrief } from './HealthCheckBrief'
import { FleetMakeupBrief } from './FleetMakeupBrief'
import { SecurityBrief } from './SecurityBrief'
import { SecurityDemoBrief } from './SecurityDemoBrief' /* DEMO ONLY */
import {
  BOARD_COLUMNS,
  BoardColumn,
  isLifecycleStage,
  lifecycleStageFor,
  LifecycleStage,
  stageOrDefault,
} from './lifecycle'
import styles from './BriefsHub.module.css'
import { useDemoOptions } from './DemoOptionsPanel' /* DEMO ONLY */
import tableStyles from './CustomerIntelligenceTable.module.css'
import { BriefThumb } from './BriefThumb'
import { ReactComponent as HealthCheckArt } from '../../assets/img/brief-thumb-health-check.svg' /* DEMO ONLY */
import { ReactComponent as FleetMakeupArt } from '../../assets/img/brief-thumb-fleet-makeup.svg' /* DEMO ONLY */
import { ReactComponent as SecurityArt } from '../../assets/img/brief-thumb-security.svg' /* DEMO ONLY */

interface Props {
  teamID: string
  host: HostData
}

type ThumbBrief = React.ComponentProps<typeof BriefThumb>['brief']

/* a brief on the board: one that can be opened (with its link and thumbnail), or one that's coming soon */
interface BoardBrief {
  title: string
  /** where it opens, and the brief it is (its thumbnail and data-brief): absent while it's coming soon */
  open?: { to: string; brief: string; thumb: ThumbBrief }
  /** just made available: flagged New */
  isNew?: boolean
}

/* a wallet: the briefs for one job, in one lifecycle column (stacked back to front, in this order) */
interface Wallet {
  column: BoardColumn
  name: string
  briefs: BoardBrief[]
}

const HEALTH_CHECK = { to: 'health-check', brief: 'Health check', thumb: 'health-check' as const }
const SECURITY = { to: 'security', brief: 'Security', thumb: 'security' as const }

const WALLETS: Wallet[] = [
  {
    column: 'trial',
    name: 'Proposal & planning',
    briefs: [
      { title: 'Industry benchmark' },
      // DEMO ONLY: a mock of the Security brief, made-up data (SecurityDemoBrief)
      { title: 'Security (DEMO)', open: { to: 'security-demo', brief: 'Security (DEMO)', thumb: 'security' } },
      { title: 'Volume forecasting/trends' },
    ],
  },
  {
    column: 'onboarding',
    name: 'Feature usage',
    briefs: [{ title: 'Feature utilization' }, { title: 'Environment, social & governance' }],
  },
  {
    column: 'onboarding',
    name: 'System usage',
    briefs: [
      { title: 'System stability' },
      { title: 'Business review' },
      { title: 'Printer security checklist', open: SECURITY },
    ],
  },
  {
    column: 'serve',
    name: 'Activity overview',
    briefs: [{ title: 'Business review' }, { title: 'Feature utilization' }, { title: 'Volume forecasting/trends' }],
  },
  {
    column: 'serve',
    name: 'System health',
    briefs: [
      { title: 'Print security checkup', open: SECURITY },
      { title: 'Health check', open: HEALTH_CHECK, isNew: true },
    ],
  },
  {
    column: 'upsell',
    name: 'Opportunities',
    briefs: [
      { title: 'Fleet makeup', open: { to: 'fleet-makeup', brief: 'Fleet makeup', thumb: 'fleet-makeup' } },
      { title: 'Additional embeddeds & licenses' },
      { title: 'License & feature expansion' },
      { title: 'Benchmark & compare' },
      { title: 'Business review' },
      { title: 'Volume forecasting/trends' },
    ],
  },
  {
    column: 'retention',
    name: 'Renewal maximizer',
    briefs: [{ title: 'Business review' }, { title: 'Health check', open: HEALTH_CHECK }],
  },
  { column: 'expired', name: 'Win back', briefs: [{ title: 'Win-back proposal' }] },
]

/* a brief's title, flagged New when it's just been made available (in place of a tip over the board) */
const BriefTitle: FunctionComponent<{ brief: BoardBrief }> = ({ brief }) => (
  <span>
    {brief.title}
    {brief.isNew && <span className={styles.newBadge}>New</span>}
  </span>
)

/* DEMO ONLY: an item's place in its view's entrance (rows, then books, one after another), as --i */
const entranceOrder = (i: number) => (el: HTMLElement | null) => el?.style.setProperty('--i', String(i))

/* one wallet of briefs. With coming-soon briefs folded away (DEMO ONLY: a display preference), they become one card
   at the back of the wallet, "+N coming soon", which opens them out */
const WalletCard: FunctionComponent<{ wallet: Wallet; showComingSoon: boolean }> = ({ wallet, showComingSoon }) => {
  const [opened, setOpened] = useState(false)
  const comingSoon = wallet.briefs.filter(brief => !brief.open).length
  const folded = !showComingSoon && !opened && comingSoon > 0
  const briefs = folded ? wallet.briefs.filter(brief => brief.open) : wallet.briefs
  return (
    <div className='wallet fp'>
      <div className='list condensed stacked' data-list='briefings'>
        <ul className='no-margin-bottom'>
          {folded && (
            <li>
              <div className='row row-link'>
                <div className='col title'>
                  <button type='button' className={styles.moreComingSoon} onClick={() => setOpened(true)}>
                    +{comingSoon} coming soon
                  </button>
                </div>
              </div>
            </li>
          )}
          {briefs.map(brief =>
            brief.open ? (
              <li key={brief.title} data-highlight=''>
                <Link to={brief.open.to} className='row row-link' data-brief={brief.open.brief}>
                  <BriefThumb brief={brief.open.thumb} className={styles.briefWatermark} />
                  <div className='col title'>
                    <div className='ellipsis'>
                      <BriefTitle brief={brief} />
                    </div>
                  </div>
                </Link>
              </li>
            ) : (
              <li key={brief.title}>
                <div className='row row-link has-tip'>
                  <span className='tip center'>Coming soon</span>
                  <div className='col title'>
                    <div className='ellipsis'>{brief.title}</div>
                  </div>
                </div>
              </li>
            )
          )}
        </ul>
      </div>
      <span className='leather'>
        <h5>{wallet.name}</h5>
      </span>
    </div>
  )
}

/* each brief once (the board repeats some in several wallets), with the stages and jobs it's for: the ones to
   open first, then the coming-soon ones, each in lifecycle order */
interface BriefRow {
  brief: BoardBrief
  columns: BoardColumn[]
  jobs: string[]
}

const briefRows = (wallets: Wallet[]): BriefRow[] => {
  const rows = new Map<string, BriefRow>()
  wallets.forEach(wallet =>
    wallet.briefs.forEach(brief => {
      const row = rows.get(brief.title) ?? { brief, columns: [], jobs: [] }
      if (!row.columns.includes(wallet.column)) row.columns.push(wallet.column)
      if (!row.jobs.includes(wallet.name)) row.jobs.push(wallet.name)
      row.brief = { ...row.brief, isNew: row.brief.isNew || brief.isNew }
      rows.set(brief.title, row)
    })
  )
  const all = Array.from(rows.values())
  return [...all.filter(row => row.brief.open), ...all.filter(row => !row.brief.open)]
}

const columnLabel = (column: BoardColumn) => BOARD_COLUMNS.find(c => c.value === column)?.label ?? column

/* a lifecycle column's name beside a dot in its colour, as in the customers table */
const StageName: FunctionComponent<{ column: BoardColumn; current: boolean }> = ({ column, current }) => (
  <span className={styles.stageName} data-column={column} data-current={current || undefined}>
    {columnLabel(column)}
  </span>
)

/* DEMO ONLY: the briefs as a list, styled as the customers table: a row per brief (the ready ones open from
   anywhere on the row), its stages (the customer's current one in bold) and jobs, and its status. Coming-soon
   briefs can be folded away behind a button, as on the board */
const BriefsTable: FunctionComponent<{
  wallets: Wallet[]
  current: BoardColumn[]
  currentLabel: string
  showComingSoon: boolean
}> = ({ wallets, current, currentLabel, showComingSoon }) => {
  const [opened, setOpened] = useState(false)
  const navigate = useNavigate()
  const rows = briefRows(wallets)
  const comingSoon = rows.filter(row => !row.brief.open).length
  const shown = showComingSoon || opened ? rows : rows.filter(row => row.brief.open)
  const firstCurrentRow = shown.findIndex(row => row.columns.some(column => current.includes(column)))

  return (
    <>
      <div className={`${tableStyles.customerTableContainer} ${styles.listTable}`}>
        <table className={tableStyles.customerTable} aria-label='Briefs'>
          <thead>
            <tr>
              <th>Brief</th>
              <th>Lifecycle stage</th>
              <th>Used for</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(({ brief, columns: briefColumns, jobs }, i) => {
              const { open } = brief
              // the customer's current stage is flagged once, beside it in the first row it's in
              const flagged = i === firstCurrentRow ? briefColumns.find(column => current.includes(column)) : undefined
              return (
                <tr
                  key={brief.title}
                  ref={entranceOrder(i)}
                  className={`${styles.listRow} ${open ? tableStyles.customerTableRow : styles.listComingSoonRow}`}
                  onClick={open ? () => navigate(open.to) : undefined}
                >
                  <td className={styles.listBriefCell}>
                    {/* the brief's artwork in an angled panel down the row's left edge, as the Intel tab's section
                        headers have their icons (a coming-soon brief's in grey, as on the board) */}
                    <span className={styles.listArt} data-coming-soon={!open || undefined} aria-hidden='true'>
                      <BriefThumb brief={open ? open.thumb : 'health-check'} className={styles.listArtImage} />
                    </span>
                    {open ? (
                      <Link
                        to={open.to}
                        className={tableStyles.customerNameLink}
                        onClick={event => event.stopPropagation()}
                      >
                        <BriefTitle brief={brief} />
                      </Link>
                    ) : (
                      brief.title
                    )}
                  </td>
                  <td>
                    <span className={styles.stageNames}>
                      {briefColumns.map(column => (
                        <span key={column} className={styles.stageNameWithChip}>
                          <StageName column={column} current={current.includes(column)} />
                          {column === flagged && (
                            <span className={styles.currentChip} title={`Current stage: ${currentLabel}`}>
                              Current
                            </span>
                          )}
                        </span>
                      ))}
                    </span>
                  </td>
                  <td>{jobs.join(', ')}</td>
                  <td className={styles.listStatus}>
                    {open ? 'Ready' : <span className={styles.comingSoon}>Coming soon</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {!showComingSoon && !opened && comingSoon > 0 && (
        <button type='button' className={`btn secondary sml ${styles.listMore}`} onClick={() => setOpened(true)}>
          Show {comingSoon} coming soon
        </button>
      )}
    </>
  )
}

/* DEMO ONLY: the briefs as books on shelves, a shelf per lifecycle stage, its books grouped by job (as the
   board's wallets group them, each group labelled on the shelf under it). The ones ready to open stand face out:
   covers in the stage's colour, with the brief's artwork as a watermark and its title at the foot,
   lifting when hovered. The coming-soon ones stand beside them spine out, their titles running up the spine; with
   coming-soon briefs folded away, a group's spines become one, "+N" */
/* how the books vary as they stand: a few heights and widths for the spines, in turn, and the last spine of a group
   leaning against the air beside it */
const SPINE_VARIANTS = 5

/* the brief's artwork drawn inline on its cover, so CSS can recolour it in the cover's own tones, as the Info
   Center's documents have theirs */
const BOOK_ART: Record<ThumbBrief, FunctionComponent<React.SVGProps<SVGSVGElement>>> = {
  'health-check': HealthCheckArt,
  'fleet-makeup': FleetMakeupArt,
  security: SecurityArt,
}

/* the stage's colour in a few shades, from its darkest: the covers along a shelf take them in turn, as do its
   spines (starting a shade lighter, so a cover stands out from the spines beside it) */
const COVER_SHADES = 4
/* and the covers stand at a few heights, as real books do */
const BOOK_HEIGHTS = 3

const BookGroup: FunctionComponent<{
  wallet: Wallet
  showComingSoon: boolean
  first: number
  /** how many covers, and how many spines, stand on the shelf before this group's */
  shade: { covers: number; spines: number }
}> = ({ wallet, showComingSoon, first, shade }) => {
  const [opened, setOpened] = useState(false)
  const ready = wallet.briefs.filter(brief => brief.open)
  const comingSoon = wallet.briefs.filter(brief => !brief.open)
  const folded = !showComingSoon && !opened && comingSoon.length > 0
  const spines = folded ? 1 : comingSoon.length
  // a lean only with company: a group of one stands up straight
  const leans = (spine: number) => ready.length + spines > 1 && spine === spines - 1
  return (
    <li className={styles.bookGroup}>
      <ul className={styles.books} aria-label={wallet.name}>
        {ready.map((brief, i) => {
          const open = brief.open as NonNullable<BoardBrief['open']>
          const Art = BOOK_ART[open.thumb]
          return (
            <li key={brief.title} ref={entranceOrder(first + i)} className={styles.bookSlot} data-cover>
              <Link
                to={open.to}
                className={styles.book}
                data-column={wallet.column}
                data-shade={(shade.covers + i) % COVER_SHADES}
                data-height={(first + i) % BOOK_HEIGHTS}
                data-brief={open.brief}
              >
                <Art className={styles.bookWatermark} preserveAspectRatio='xMinYMin slice' aria-hidden='true' />
                <span className={styles.bookTitle}>
                  <BriefTitle brief={brief} />
                </span>
              </Link>
            </li>
          )
        })}
        {folded ? (
          <li ref={entranceOrder(first + ready.length)} className={styles.bookSlot} data-lean={leans(0) || undefined}>
            <button
              type='button'
              className={styles.spine}
              data-variant={0}
              data-column={wallet.column}
              data-shade={(shade.spines + 1) % COVER_SHADES}
              onClick={() => setOpened(true)}
            >
              <span>+{comingSoon.length} coming soon</span>
            </button>
          </li>
        ) : (
          comingSoon.map((brief, i) => (
            <li
              key={brief.title}
              ref={entranceOrder(first + ready.length + i)}
              className={styles.bookSlot}
              data-lean={leans(i) || undefined}
            >
              <span
                className={`${styles.spine} has-tip`}
                data-variant={(first + i) % SPINE_VARIANTS}
                data-column={wallet.column}
                data-shade={(shade.spines + i + 1) % COVER_SHADES}
              >
                <span className='tip center'>Coming soon</span>
                <span>{brief.title}</span>
              </span>
            </li>
          ))
        )}
      </ul>
      <h5 className={styles.bookGroupName}>{wallet.name}</h5>
    </li>
  )
}

const Shelf: FunctionComponent<{
  column: BoardColumn
  wallets: Wallet[]
  current: boolean
  showComingSoon: boolean
  /** where its books come in, after the shelves before it */
  first: number
}> = ({ column, wallets, current, showComingSoon, first }) => {
  const groups = wallets.filter(wallet => wallet.column === column)
  if (groups.length === 0) return null
  // each group's books come in after the group before it's
  const starts = groups.reduce<number[]>(
    (list, wallet, i) => [...list, i === 0 ? first : list[i - 1] + groups[i - 1].briefs.length],
    []
  )
  const before = (i: number, coming: boolean) =>
    groups.slice(0, i).reduce((n, wallet) => n + wallet.briefs.filter(brief => !brief.open === coming).length, 0)
  return (
    <section className={styles.shelf} data-column={column} aria-label={`${columnLabel(column)} briefs`}>
      <h4 className={styles.shelfTitle}>
        <StageName column={column} current={current} />
        {current && <span className={styles.currentChip}>Current stage</span>}
      </h4>
      <ul className={styles.bookGroups}>
        {groups.map((wallet, i) => (
          <BookGroup
            key={wallet.name}
            wallet={wallet}
            showComingSoon={showComingSoon}
            first={starts[i]}
            shade={{ covers: before(i, false), spines: before(i, true) }}
          />
        ))}
      </ul>
    </section>
  )
}

/* the customer's lifecycle stage, as set on the Intel tab (undefined when it isn't set), and whether it has loaded */
const useLifecycleStage = (teamID: string, customerID: string): { stage?: LifecycleStage; loaded: boolean } => {
  const [stage, setStage] = useState<LifecycleStage>()
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    setLoaded(false)
    const controller = new AbortController()
    const request = axios.get<{ lifecycleStage?: string }>(`/api/teams/${teamID}/customers/${customerID}/profile`, {
      signal: controller.signal,
    })
    if (request && typeof request.then === 'function') {
      request
        .then(response => {
          const value = response?.data?.lifecycleStage
          setStage(isLifecycleStage(value) ? value : undefined)
          setLoaded(true)
        })
        .catch(err => {
          if (!axios.isCancel(err)) setLoaded(true) // no stage: the board shows its default
        })
    } else {
      setLoaded(true)
    }
    return () => controller.abort()
  }, [teamID, customerID])
  return { stage, loaded }
}

/* each layout's heading and introduction */
const INTROS: Record<'board' | 'list' | 'shelf', { title: string; description: string }> = {
  board: {
    title: 'Briefs by lifecycle',
    description: "Curated, point-in-time views of the customer's PaperCut deployment across their lifecycle.",
  },
  list: {
    title: 'All briefs',
    description: 'Every brief for this customer, with the lifecycle stages and jobs it is for.',
  },
  shelf: {
    title: 'Brief library',
    description: "The briefs for each stage of the customer's lifecycle, shelved by the job they do.",
  },
}

const BriefsList: FunctionComponent<{ teamID: string; customerID: string }> = ({ teamID, customerID }) => {
  // the stage set on the Intel tab is the current stage (Serve & upsell until one is set); it can span columns
  const lifecycle = useLifecycleStage(teamID, customerID)
  const stage = stageOrDefault(lifecycle.stage)
  const current: BoardColumn[] = lifecycleStageFor(stage)?.columns ?? []
  // DEMO ONLY: the list and shelves don't wait for the stage: they show straight away, and flag the current stage
  // once it has arrived (rather than flagging the default and then moving the flag)
  const flagged: BoardColumn[] = lifecycle.loaded ? current : []
  const first = current[0]
  // the Expired column only appears for an expired customer
  const expired = stage === 'expired'
  const columns = BOARD_COLUMNS.filter(column => expired || column.value !== 'expired')
  const walletsShown = WALLETS.filter(wallet => columns.some(column => column.value === wallet.column))
  // DEMO ONLY: the layout (lifecycle board, list or shelf), and whether coming-soon briefs show
  const { options } = useDemoOptions()
  const layout = options.briefsView

  // On narrow screens the lifecycle scrolls sideways; start it at the current stage rather than Trial.
  const processRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const strip = processRef.current
    if (!strip || strip.scrollWidth <= strip.clientWidth) return
    const col = strip.querySelector(`header[data-content="${first}"]`)?.closest('.col')
    if (!col) return
    strip.scrollLeft = col.getBoundingClientRect().left - strip.getBoundingClientRect().left - 8
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run when the stage arrives
  }, [first, lifecycle.loaded])

  return (
    <div>
      <header className={styles.header}>
        <h2>
          {INTROS[layout].title} <span className={styles.comingSoon}>More briefs coming soon</span>
        </h2>
      </header>
      <p>{INTROS[layout].description}</p>

      {/* DEMO ONLY: the briefs as a list, its current stage in bold */}
      {layout === 'list' && (
        <BriefsTable
          wallets={walletsShown}
          current={flagged}
          currentLabel={lifecycleStageFor(stage)?.label ?? ''}
          showComingSoon={options.showComingSoonBriefs}
        />
      )}

      {/* DEMO ONLY: the briefs on shelves, a shelf per stage */}
      {layout === 'shelf' && (
        <div className={styles.shelves}>
          {columns.map(({ value }, i) => (
            <Shelf
              key={value}
              column={value}
              wallets={walletsShown}
              current={flagged.includes(value)}
              showComingSoon={options.showComingSoonBriefs}
              first={i * 3}
            />
          ))}
        </div>
      )}

      {/* the board waits for the stage, so it arrives with the right columns and Current stage (no late changes) */}
      {lifecycle.loaded && layout === 'board' && (
        <div data-group='process' ref={processRef}>
          {/* Stage Headers Row: the current stage in colour, flagged "Current stage" */}
          <div className='row flex'>
            {columns.map(({ value, label }) => {
              const isCurrent = current.includes(value)
              // the flag spans every column of the current stage (Serve & upsell has two)
              const span = current.length === 1 ? styles.currentStageSingle : ''
              return (
                <div key={value} className={isCurrent ? 'col relative' : 'col'}>
                  {value === first && (
                    <div className={`tip visible center fp ${span}`} data-testid='current-stage'>
                      <strong>Current stage</strong>
                    </div>
                  )}
                  <header data-content={value} data-highlight={isCurrent ? '' : undefined}>
                    {label}
                  </header>
                </div>
              )
            })}
          </div>

          {/* Content & Grid Overlay Area */}
          <div className='relative'>
            <div className='row flex' data-content='grid-overlay'>
              {columns.map(({ value }) => (
                <div key={value} className='col'></div>
              ))}
            </div>

            <div className='row flex'>
              {columns.map(({ value }) => (
                <div key={value} className='col' data-content={value}>
                  {walletsShown
                    .filter(wallet => wallet.column === value)
                    .map(wallet => (
                      <WalletCard key={wallet.name} wallet={wallet} showComingSoon={options.showComingSoonBriefs} />
                    ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export const BriefsHub: FunctionComponent<Props> = ({ teamID, host }) => (
  <Routes>
    <Route index element={<BriefsList teamID={teamID} customerID={host.id} />} />
    <Route path='health-check' element={<HealthCheckBrief teamID={teamID} host={host} />} />
    <Route path='fleet-makeup' element={<FleetMakeupBrief teamID={teamID} host={host} />} />
    <Route path='security' element={<SecurityBrief teamID={teamID} host={host} />} />
    {/* DEMO ONLY */}
    <Route path='security-demo' element={<SecurityDemoBrief teamID={teamID} host={host} />} />
    <Route path='*' element={<Navigate to='.' replace />} />
  </Routes>
)
